import type {
  SequenceCheckpoint,
  SequenceOperation,
} from "@history/operations";
import type { SequenceKind } from "@lib/genetics";
import { db } from "./db";
import { getEncoder } from "./encoding";

type AddSequenceOperation = { kind: "add_sequence"; sequenceId: string };
type RemoveSequenceOperation = { kind: "remove_sequence"; sequenceId: string };

export type CollectionMembershipOperation =
  | AddSequenceOperation
  | RemoveSequenceOperation;

export type CollectionOperation = {
  id: string;
  collectionFileId: string;
  index: number;
  operation: CollectionMembershipOperation;
  timestamp: Date;
  fingerprint: string;
};

/**
 * Compute a SHA-256 content hash of a sequence string.
 *
 * Returns a hex-encoded hash. Called during flush, not on every keystroke.
 */
export async function computeContentHash(sequence: string): Promise<string> {
  const encoded = new TextEncoder().encode(sequence);
  const buffer = await crypto.subtle.digest("SHA-256", encoded);
  const bytes = new Uint8Array(buffer);

  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Get the latest content hash for a sequence by checking operations first,
 * then falling back to the initial checkpoint.
 */
export async function getLatestContentHash(
  sequenceId: string,
): Promise<string> {
  const lastOp = await db.sequenceOperations
    .where("[sequenceId+index]")
    .between([sequenceId, -Infinity], [sequenceId, Infinity])
    .last();

  if (lastOp) {
    return lastOp.contentHash;
  }

  const checkpoint = await db.sequenceCheckpoints
    .where("[sequenceId+operationIndex]")
    .equals([sequenceId, -1])
    .first();

  if (checkpoint) {
    return checkpoint.contentHash;
  }

  const sequence = await db.sequences.get(sequenceId);
  if (sequence?.contentHash) {
    return sequence.contentHash;
  }

  throw new Error(`No content hash found for sequence ${sequenceId}`);
}

/**
 * Compute a deterministic fingerprint for a collection based on its member
 * sequences and their content hashes.
 *
 * Sorts (sequenceId, contentHash) pairs to ensure determinism regardless of
 * the order sequences appear in the collection's content array.
 */
export async function computeCollectionFingerprint(
  sequenceIds: string[],
): Promise<string> {
  const pairs = await Promise.all(
    sequenceIds.map(async (id) => {
      const hash = await getLatestContentHash(id);
      return `${id}:${hash}`;
    }),
  );

  pairs.sort();

  return computeContentHash(pairs.join("\n"));
}

/**
 * Load the next operation index for a collection from IndexedDB.
 *
 * Returns 0 if no operations exist yet.
 */
export async function loadNextCollectionOperationIndex(
  collectionFileId: string,
): Promise<number> {
  const last = await db.collectionOperations
    .where("[collectionFileId+index]")
    .between([collectionFileId, -Infinity], [collectionFileId, Infinity])
    .last();

  return last ? last.index + 1 : 0;
}

/**
 * Create the initial checkpoint for a newly created sequence.
 *
 * Should be called after every `db.sequences.put()` to establish the baseline
 * for the operation log.
 */
export async function createInitialCheckpoint(
  sequenceId: string,
  sequence: string,
  kind: SequenceKind = "nt",
): Promise<void> {
  const contentHash = await computeContentHash(sequence);
  const encode = getEncoder(kind);

  const checkpoint: SequenceCheckpoint = {
    id: crypto.randomUUID(),
    sequenceId,
    operationIndex: -1,
    contentHash,
    content: encode(sequence),
    length: sequence.length,
  };

  await db.sequenceCheckpoints.put(checkpoint);
}

/**
 * Persist prepared history and its matching sequence content atomically.
 * Hashing and encoding must finish before entering this transaction.
 */
export async function flushSequenceOperations(
  operations: SequenceOperation[],
  checkpoints: SequenceCheckpoint[],
  content?: { sequenceId: string; length: number; data: Uint8Array },
): Promise<void> {
  await db.transaction(
    "rw",
    db.sequences,
    db.sequenceChunks,
    db.sequenceOperations,
    db.sequenceCheckpoints,
    async () => {
      if (content) {
        await db.sequences.update(content.sequenceId, {
          length: content.length,
        });
        await db.sequenceChunks.put({
          sequenceId: content.sequenceId,
          index: 0,
          data: content.data,
        });
      }
      for (const op of operations) {
        await db.sequenceOperations.put(op);
      }
      for (const cp of checkpoints) {
        await db.sequenceCheckpoints.put(cp);
      }
    },
  );
}

/** Log membership using the collection's sequence IDs after the change. */
export async function logCollectionOperation(
  collectionFileId: string,
  operation: CollectionMembershipOperation,
  currentSequenceIds: string[],
): Promise<void> {
  const [nextIndex, fingerprint] = await Promise.all([
    loadNextCollectionOperationIndex(collectionFileId),
    computeCollectionFingerprint(currentSequenceIds),
  ]);

  await db.collectionOperations.put({
    id: crypto.randomUUID(),
    collectionFileId,
    index: nextIndex,
    operation,
    timestamp: new Date(),
    fingerprint,
  });
}
